import { useQuery } from '@tanstack/react-query';
import { getDashboardStats } from '@app/services/admin';
import UsersIcon from '@app/assets/icons/grid/users.svg?react';
import BriefcaseGrayIcon from '@app/assets/icons/grid/briefcase-gray.svg?react';
import OpenEnvelopeIcon from '@app/assets/icons/grid/envelope.svg?react';
import UsageIcon from '@app/assets/icons/menu/usage.svg?react';
import { Link } from 'react-router-dom';

export const AdminDashboard = () => {
  const {
    data: stats,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: getDashboardStats,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome to the Echo Ratings Admin Panel
          </p>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading dashboard statistics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome to the Echo Ratings Admin Panel
          </p>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-red-500">Error loading dashboard statistics</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome to the Echo Ratings Admin Panel
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Businesses Card */}
        <div className="bg-white p-3 sm:p-4 rounded-lg border">
          <div className="flex gap-2 p-3 sm:p-4">
            <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <BriefcaseGrayIcon
                width={20}
                height={20}
                className="sm:w-6 sm:h-6"
              />
            </div>
            <div className="flex-1">
              <span className="font-bold text-lg sm:text-xl">
                {stats?.totalBusinesses?.toLocaleString() || '0'}
              </span>
              <p className="text-base-coolGray text-xs sm:text-sm">
                Total Businesses
              </p>
            </div>
          </div>
        </div>

        {/* Total Users Card */}
        <div className="bg-white p-3 sm:p-4 rounded-lg border">
          <div className="flex gap-2 p-3 sm:p-4">
            <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <UsersIcon width={20} height={20} className="sm:w-6 sm:h-6" />
            </div>
            <div className="flex-1">
              <span className="font-bold text-lg sm:text-xl">
                {stats?.totalUsers?.toLocaleString() || '0'}
              </span>
              <p className="text-base-coolGray text-xs sm:text-sm">
                Total Users
              </p>
            </div>
          </div>
        </div>

        {/* Total Campaigns Card */}
        <div className="bg-white p-3 sm:p-4 rounded-lg border">
          <div className="flex gap-2 p-3 sm:p-4">
            <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <OpenEnvelopeIcon
                width={20}
                height={20}
                className="sm:w-6 sm:h-6"
              />
            </div>
            <div className="flex-1">
              <span className="font-bold text-lg sm:text-xl">
                {stats?.totalCampaigns?.toLocaleString() || '0'}
              </span>
              <p className="text-base-coolGray text-xs sm:text-sm">
                Total Campaigns
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Quick Actions
          </h3>
          <div className="mt-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin/businesses"
                className="bg-white p-3 sm:p-4 rounded-lg border hover:shadow-md transition-shadow"
              >
                <div className="flex gap-2 p-3 sm:p-4">
                  <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                    <BriefcaseGrayIcon
                      width={20}
                      height={20}
                      className="sm:w-6 sm:h-6"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-lg sm:text-xl">
                      Businesses Management
                    </span>
                    <p className="text-base-coolGray text-xs sm:text-sm">
                      Edit, and manage businesses
                    </p>
                  </div>
                </div>
              </Link>

              <Link
                to="/admin/users"
                className="bg-white p-3 sm:p-4 rounded-lg border hover:shadow-md transition-shadow"
              >
                <div className="flex gap-2 p-3 sm:p-4">
                  <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                    <UsersIcon
                      width={20}
                      height={20}
                      className="sm:w-6 sm:h-6"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-lg sm:text-xl">
                      Users Management
                    </span>
                    <p className="text-base-coolGray text-xs sm:text-sm">
                      Create, edit, and manage user accounts
                    </p>
                  </div>
                </div>
              </Link>

              <Link
                to="/admin/plans"
                className="bg-white p-3 sm:p-4 rounded-lg border hover:shadow-md transition-shadow"
              >
                <div className="flex gap-2 p-3 sm:p-4">
                  <div className="bg-base-antiFlash rounded-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                    <UsageIcon
                      width={20}
                      height={20}
                      className="sm:w-6 sm:h-6"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-lg sm:text-xl">
                      Plans Management
                    </span>
                    <p className="text-base-coolGray text-xs sm:text-sm">
                      Configure subscription plans
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
